import React, { ReactNode, useMemo } from 'react';
import { ApolloError, gql } from '@apollo/client';
import { Breakpoint, Constrain, Paginator } from '@moda/om';
import loadable from '@loadable/component';
import { compact } from '@moda/portal-stanchions';
import { SortOption, DEFAULT_SORT_OPTIONS } from '../../constants';
import { usePLPParams } from '../../hooks/usePLPParams';
import { BackToTop } from '../BackToTop/BackToTop';
import { Loading } from '../Loading';
import { LazyGrid, GridLoading, PlaceholderCell } from '../Grid';
import {
  PAGE_FILTER_AVAILABILITY_FACET_FRAGMENT,
  PAGE_FILTER_FRAGMENT,
  PAGE_FILTER_FACET_FRAGMENT,
  PAGE_FILTER_NAME_SLUG_FACET_FRAGMENT,
  PAGE_FILTER_SIZE_FACET_FRAGMENT
} from '../PageFilter';
import { VARIANT_CELL_FRAGMENT } from '../Grid/GridCell/VariantCell/VariantCell';
import { SchemaOrgProductListing, SCHEMA_ORG_PRODUCT_LISTING_FRAGMENT } from '../SchemaOrg';
import { ProductListPageResultFragment } from '../../generated/types/ProductListPageResultFragment';
import { GuidePageQuery_displayPage as DisplayPage } from '../../generated/types/GuidePageQuery';
import { throwQueryError } from '../../lib/apollo/throwQueryError';
import { PageMeta } from '../PageMeta';
import { PRODUCTS_PER_PAGE } from '../../constants';
import { useFilterEngagementTracking } from '../../hooks/useFilterEngagementTracking';
import { usePLPTracking } from '../../hooks/usePLPTracking';
import { useAdminMode } from '../AdminMode/AdminModeProvider';
import { tc } from '../../lib/trackingContext';
import { ProductListingDesktop } from './ProductListingDesktop';
import { ProductListingMobile } from './ProductListingMobile';
import { useRedirectLegacyPLPQueryParams } from './useRedirectLegacyPLPQueryParams';
import { ProductListingEmptyState } from './ProductListingEmptyState';
import { PRODUCT_LISTING_VISUAL_STYLE_FILTER_FRAGMENT } from './ProductListingVisualStyleFilters';

import './ProductListing.scss';

export const PRODUCT_LIST_PAGE_VARIANT_EDGE_FRAGMENT = gql`
  fragment ProductListPageVariantEdgeFragment on VariantEdge {
    node {
      ...VariantCellFragment
      ...SchemaOrgProductListingFragment
    }
  }
  ${VARIANT_CELL_FRAGMENT}
  ${SCHEMA_ORG_PRODUCT_LISTING_FRAGMENT}
`;

export const PRODUCT_LIST_PAGE_RESULT_FRAGMENT = gql`
  fragment ProductListPageResultFragment on CollectionResult {
    attributes {
      ...PageFilterNameSlugFacetFragment
    }
    availability {
      ...PageFilterAvailabilityFacetFragment
    }
    designers {
      ...PageFilterNameSlugFacetFragment
    }
    filterColors: filter_colors {
      ...PageFilterFacetFragment
    }
    materials {
      ...PageFilterNameSlugFacetFragment
    }
    saleTags: sale_tags {
      ...PageFilterFacetFragment
    }
    sizes {
      ...PageFilterSizeFacetFragment
    }
    totalItems: total_items
    variants(after: $after) {
      edges {
        ...ProductListPageVariantEdgeFragment
      }
    }
    ...PageFilterFragment
    ...ProductListingVisualStyleFilterFragment
  }
  ${PRODUCT_LIST_PAGE_VARIANT_EDGE_FRAGMENT}
  ${PAGE_FILTER_AVAILABILITY_FACET_FRAGMENT}
  ${PAGE_FILTER_FACET_FRAGMENT}
  ${PAGE_FILTER_NAME_SLUG_FACET_FRAGMENT}
  ${PAGE_FILTER_SIZE_FACET_FRAGMENT}
  ${PAGE_FILTER_FRAGMENT}
  ${PRODUCT_LISTING_VISUAL_STYLE_FILTER_FRAGMENT}
`;

type ProductListingProps = {
  title: string;
  loading: boolean;
  headerText?: string;
  error?: ApolloError;
  description?: string;
  showPLPBreadcrumbs?: boolean;
  availableSorts?: SortOption[];
  displayPage?: DisplayPage | null;
  queryResult?: ProductListPageResultFragment | null;
  belowHeader?: ReactNode;
};

export type ProductListingPath = [string, string];

const VariantCell = loadable(() => import('../Grid/GridCell/VariantCell/VariantCell'), {
  resolveComponent: exports => exports.VariantCell,
  fallback: <PlaceholderCell />
});

const AdminModePLPScoringInfo = loadable(() => import('../AdminMode/AdminModePLPScoringInfo'), {
  resolveComponent: exports => exports.AdminModePLPScoringInfo
});

export const ProductListing: React.FC<ProductListingProps> = ({
  title,
  description,
  headerText,
  queryResult,
  availableSorts = DEFAULT_SORT_OPTIONS,
  displayPage,
  error,
  loading,
  children,
  showPLPBreadcrumbs = true,
  belowHeader = null
}) => {
  const {
    params: { page },
    currentSort,
    goToPage,
    changeSort
  } = usePLPParams(availableSorts);

  const onGoToPage = (page: number) => {
    goToPage(page);
    trackClick(page);
  };

  const numberOfProductsPerPage = PRODUCTS_PER_PAGE;

  const { enabled: adminModeEnabled } = useAdminMode();

  const variants = useMemo(
    () =>
      compact(
        (queryResult?.variants?.edges || [])
          .filter(edge => !!edge && !!edge.node)
          .map(edge => edge && edge.node)
      ),
    [queryResult]
  );

  useFilterEngagementTracking({
    variants,
    loading,
    availableSorts
  });

  useRedirectLegacyPLPQueryParams();

  usePLPTracking({
    variants: variants,
    title: title
  });

  if (error) {
    throwQueryError(error);
  }

  if (!queryResult) {
    return <Loading />;
  }

  const totalPages = Math.ceil((queryResult.totalItems || 0) / numberOfProductsPerPage);

  const trackClick = (page: number) =>
    tc.track('Pagination Clicked', { addToTrackingEvent: { label: String(page) || '' } });

  const content = (
    <div className="ProductListing__content">
      <LazyGrid className="ProductListing__grid">
        {variants.map((variant, index) => (
          <VariantCell key={variant.id} variant={variant} selfPosition={index + 1} />
        ))}
      </LazyGrid>

      {children}

      {queryResult?.totalItems ? (
        <Paginator
          className="ProductListing__pagination"
          totalPages={totalPages}
          selectedPage={page}
          onGoToPage={onGoToPage}
        />
      ) : (
        <ProductListingEmptyState className="ProductListing__empty-state" />
      )}

      <GridLoading show={loading} />
    </div>
  );

  return (
    <>
      <PageMeta title={title} description={description} paginated totalPages={totalPages} />
      <SchemaOrgProductListing variants={variants} />
      <Constrain>
        <div className="ProductListing">
          {adminModeEnabled && <AdminModePLPScoringInfo />}

          <Breakpoint gt="md">
            <ProductListingDesktop
              availableSorts={availableSorts}
              currentSort={currentSort}
              headerText={headerText}
              onSortChanged={changeSort}
              queryResult={queryResult}
              displayPage={displayPage}
              showPLPBreadcrumbs={showPLPBreadcrumbs}
              belowHeader={belowHeader}
            >
              {content}
            </ProductListingDesktop>
          </Breakpoint>

          <Breakpoint lt="md">
            <ProductListingMobile
              availableSorts={availableSorts}
              currentSort={currentSort}
              headerText={headerText}
              onSortChanged={changeSort}
              queryResult={queryResult}
              displayPage={displayPage}
              showPLPBreadcrumbs={showPLPBreadcrumbs}
              belowHeader={belowHeader}
            >
              {content}
            </ProductListingMobile>
          </Breakpoint>
        </div>
      </Constrain>
      <BackToTop />
    </>
  );
};
